import { useEffect, useState } from 'react';
import { fetchConfigData } from '../services/dataApi';

const FALLBACK = [{ code: 'en', label: 'English', tts: 'en-US' }];

let _cache = null;
let _promise = null;

function fetchLanguages() {
  if (_promise) return _promise;
  _promise = Promise.all([
    fetchConfigData('languages.json'),
    // Display metadata is optional; the base registry remains usable on its own.
    fetchConfigData('languages_display.json').catch(() => []),
  ]).then(([base, display]) => {
    const displayMap = Object.fromEntries(display.map((d) => [d.code, d]));
    const merged = base.map((lang) => ({ ...lang, ...displayMap[lang.code] }));
    _cache = merged.length ? merged : FALLBACK;
    return _cache;
  }).catch(() => FALLBACK)
    .finally(() => { _promise = null; });
  return _promise;
}

export function useLanguages() {
  const [languages, setLanguages] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let active = true;
    if (_cache) {
      setLanguages(_cache);
      setLoading(false);
      return;
    }
    const load = () => fetchLanguages().then((data) => {
      if (!active) return;
      setLanguages(data);
      setLoading(false);
    });
    load();
    window.addEventListener('online', load);
    return () => {
      active = false;
      window.removeEventListener('online', load);
    };
  }, []);

  return { languages, loading };
}

export function filterLanguagesByConfig(languages, configuredCodes) {
  if (configuredCodes == null) return languages;
  if (!Array.isArray(configuredCodes)) return [];
  const allowed = new Set(
    configuredCodes.map((code) => String(code).trim().toLowerCase()),
  );
  return languages.filter((language) => allowed.has(language.code));
}

export function useLangMap() {
  const { languages, loading } = useLanguages();
  const map = Object.fromEntries(languages.map((l) => [l.code, l]));
  return { langMap: map, loading };
}
